import React, { useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  Email as EmailIcon,
  Lock as LockIcon,
  Login as LoginIcon,
  PersonAdd as PersonAddIcon,
  LockReset as LockResetIcon,
} from "@mui/icons-material";
import { postData } from "../../Webservice/Webservice";
import { fieldSx, goldButtonSx } from "./signinStyles";

// Email + password sign in for one kind of account. The Customer and Business sections of the sign in page
// each keep their own copy, so typing in one never shows up in the other.
function EmailLoginForm({ accountType, onSignedIn, onError, onForgotPassword }) {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    onError("");
    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Please enter a valid email address";
    if (!formData.password) newErrors.password = "Password is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    onError("");
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await postData("customer/login", {
        email: formData.email.trim(),
        password: formData.password,
        type: "email",
        customerType: accountType,
      });
      if (accountType === "customer" && response && !response.error && !response.token) {
        // an account that is not verified yet answers without a token
        onError(response.message || "Please verify your account to continue.");
      } else if (response && !response.error) {
        onSignedIn(response);
      } else {
        onError(response?.message || "Login failed. Please check your credentials.");
      }
    } catch (error) {
      onError(error.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" noValidate onSubmit={handleSubmit}>
      <TextField
        fullWidth
        placeholder="Email address"
        inputProps={{ "aria-label": "Email address" }}
        name="email"
        type="email"
        size="small"
        value={formData.email}
        onChange={handleChange}
        variant="outlined"
        margin="dense"
        error={!!errors.email}
        helperText={errors.email}
        sx={fieldSx}
        InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: "#C8A054", fontSize: 20 }} /></InputAdornment> }}
      />

      <TextField
        fullWidth
        placeholder="Password"
        inputProps={{ "aria-label": "Password" }}
        name="password"
        type={showPassword ? "text" : "password"}
        size="small"
        value={formData.password}
        onChange={handleChange}
        variant="outlined"
        margin="dense"
        error={!!errors.password}
        helperText={errors.password}
        sx={fieldSx}
        InputProps={{
          startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: "#C8A054", fontSize: 20 }} /></InputAdornment>,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" tabIndex={-1} sx={{ color: "#8a6a24" }}>
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Typography variant="body2" align="right" sx={{ mt: 0.5, mb: 2 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onForgotPassword}
          underline="hover"
          sx={{ color: "#8a6a24", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 0.5 }}
        >
          <LockResetIcon sx={{ fontSize: 17 }} />
          Forgot Password?
        </Link>
      </Typography>

      <Button type="submit" fullWidth variant="contained" disabled={loading} startIcon={loading ? null : <LoginIcon />} sx={goldButtonSx}>
        {loading ? <CircularProgress size={22} sx={{ color: "white" }} /> : "Sign In"}
      </Button>

      <Typography variant="body2" align="center" sx={{ mt: 2.5, color: "text.secondary" }}>
        Don&apos;t have an account?{" "}
        <Link
          href={accountType === "customer" ? "/signup" : "/business/signup"}
          underline="hover"
          sx={{ color: "#8a6a24", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 0.5, verticalAlign: "bottom" }}
        >
          <PersonAddIcon sx={{ fontSize: 17 }} />
          Create account
        </Link>
      </Typography>
    </Box>
  );
}

export default EmailLoginForm;
